import {
  ActionIcon,
  Anchor,
  Group,
  Stack,
  Table,
  Text,
  Tooltip
} from '@mantine/core'
import { IconPencil, IconTrash } from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link, useLocation } from 'react-router'
import { LabelingTypeChip } from '@/components/annotations/LabelingTypes/LabelingTypeChip'
import { LabelGroup } from '@/components/labels/LabelGroup/LabelGroup'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { SortableLabel } from '@/components/ui-shared/Tables/SortableLabel/SortableLabel'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { RouterPath, replacePathParams } from '@/router/paths'
import { Dataset, DatasetType } from '@/types/dataset'
import { CrudAction, Resource } from '@/types/permissions'
import { getFormattedDate } from '@/utils/date'
import { formatSize } from '@/utils/file'
import { SortConfig } from '@/utils/sorting'
import { DatasetPreview } from '../DatasetPreview/DatasetPreview'
import { DatasetTypeChip } from '../DatasetTypeChip/DatasetTypeChip'
import { DeleteDatasetModal } from '../DeleteDatasetModal/DeleteDatasetModal'
import { UpdateDatasetModal } from '../UpdateDatasetModal/UpdateDatasetModal'
import { DatasetThumbnail } from './DatasetThumbnail'

export type TableSortConfig = SortConfig<'name' | 'created_at'>

type DatasetListProps = {
  datasets: Dataset[]
  sortConfig: TableSortConfig
  isLoading?: boolean
  hasError?: boolean
  onReload?: () => void
  onSort: (sortConfig: TableSortConfig) => void
}

export const DatasetList = ({
  datasets,
  sortConfig,
  isLoading,
  hasError,
  onReload,
  onSort
}: DatasetListProps) => {
  const location = useLocation()
  const [previewDataset, setPreviewDataset] = useState<Dataset | null>(null)
  const [datasetToEdit, setDatasetToEdit] = useState<Dataset | null>(null)
  const [datasetToRemove, setDatasetToRemove] = useState<Dataset | null>(null)

  const [canUpdateDataset, canDeleteDataset] = useHasPermissions(
    Resource.Datasets,
    [CrudAction.Update, CrudAction.Delete]
  )

  return (
    <>
      <DataTable
        hasItems={datasets.length > 0}
        isLoading={isLoading}
        hasError={hasError}
        emptyMessage={<FormattedMessage id="datasets.list.empty" />}
        onReload={onReload}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={100}>&nbsp;</Table.Th>

            <Table.Th w={240}>
              <SortableLabel
                label={<FormattedMessage id="datasets.list.columns.name" />}
                disabled={datasets.length === 0}
                sortKey="name"
                sortConfig={sortConfig}
                onSort={onSort}
              />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="datasets.list.columns.type" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="datasets.list.columns.fileCount" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="datasets.list.columns.size" />{' '}
              <Text size="xs" component="span">
                <FormattedMessage id="datasets.list.columns.size.allVersions" />
              </Text>
            </Table.Th>

            <Table.Th>
              <SortableLabel
                label={<FormattedMessage id="datasets.list.columns.created" />}
                disabled={datasets.length === 0}
                sortKey="created_at"
                sortConfig={sortConfig}
                onSort={onSort}
              />
            </Table.Th>

            <Table.Th w={300}>
              <FormattedMessage id="datasets.list.columns.labelingClasses" />
            </Table.Th>

            <Table.Th>&nbsp;</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {datasets.map((dataset) => (
            <Table.Tr key={dataset.id}>
              <Table.Td>
                <DatasetThumbnail
                  imageUrl={dataset.thumbnail || undefined}
                  onPreview={() => setPreviewDataset(dataset)}
                />
              </Table.Td>

              <Table.Td>
                <Anchor
                  component={Link}
                  size="sm"
                  underline="hover"
                  c="blue.8"
                  fw="bold"
                  to={replacePathParams(RouterPath.datasetDetails, {
                    datasetId: dataset.id
                  })}
                  state={{ datasetListParams: location.search }}
                >
                  {dataset.name}
                </Anchor>
              </Table.Td>

              <Table.Td>
                <Stack gap={4}>
                  <LabelingTypeChip labelingType={dataset.labeling_type} />

                  {dataset.dataset_type === DatasetType.Secondary && (
                    <DatasetTypeChip datasetType={dataset.dataset_type} />
                  )}
                </Stack>
              </Table.Td>

              <Table.Td>
                <FormattedMessage
                  id="datasets.fileCount"
                  values={{ count: dataset.version_files_count }}
                />
              </Table.Td>

              <Table.Td fz={11} fw={600}>
                {formatSize(dataset.size_mb, 'mb')}
              </Table.Td>

              <Table.Td>
                {getFormattedDate(dataset.created_at, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </Table.Td>

              <Table.Td>
                <LabelGroup labels={dataset.labels} maxLabelsShown={5} />
              </Table.Td>

              <Table.Td>
                <Group wrap="nowrap" gap={4} justify="end">
                  {canUpdateDataset && (
                    <Tooltip
                      label={
                        <FormattedMessage id="datasets.list.buttons.edit" />
                      }
                    >
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        radius="xl"
                        onClick={() => setDatasetToEdit(dataset)}
                      >
                        <IconPencil style={{ width: '80%', height: '80%' }} />
                      </ActionIcon>
                    </Tooltip>
                  )}

                  {canDeleteDataset && (
                    <Tooltip label={<FormattedMessage id="delete" />}>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        radius="xl"
                        onClick={() => setDatasetToRemove(dataset)}
                      >
                        <IconTrash style={{ width: '70%', height: '70%' }} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </DataTable>

      <DatasetPreview
        opened={!!previewDataset}
        datasetId={previewDataset?.id}
        onClose={() => setPreviewDataset(null)}
      />

      <UpdateDatasetModal
        opened={!!datasetToEdit}
        datasetId={datasetToEdit?.id}
        onClose={() => setDatasetToEdit(null)}
      />

      <DeleteDatasetModal
        opened={!!datasetToRemove}
        datasetId={datasetToRemove?.id}
        onClose={() => setDatasetToRemove(null)}
      />
    </>
  )
}
